// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkmark from "@store/Library/Components/Toolkit/Icons/Checkmark";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import expandProps from "@toolkit/Framework/Common/Utilities/expandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { css } from "styled-components";

const numberSize = pxToRem(28);
const lineSize = pxToRem(2);

export interface ProgressStepperPresentationProps extends ExpandStyles<ProgressStepperStyles> {}

export interface ProgressStepperProps extends ProgressStepperPresentationProps {
    /** 1-based index of the currently active step. When omitted, no step is highlighted. */
    activeStep?: number;
    /** Ordered list of step labels. The number of steps is derived from this array's length. */
    steps: string[];
}

export interface ProgressStepperStyles {
    completeIcon?: IconPresentationProps;
    stepContent?: InjectableCss;
    stepLabel?: TypographyPresentationProps;
    stepNumber?: TypographyPresentationProps;
    stepWrapper?: InjectableCss;
    wrapper?: InjectableCss;
}

export const progressStepperStyles: ProgressStepperStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            width: 100%;
        `,
    },
    stepWrapper: {
        css: css`
            --number-box-color: ${getColor("common.borderLight")};
            --number-background-color: ${getColor("common.accent")};
            --number-text-color: ${getColor("text.lighter")};
            --number-border-width: 1px;
            --label-color: ${getColor("text.lighter")};
            --line-color-left: ${getColor("common.borderLight")};
            --line-color-right: ${getColor("common.borderLight")};

            position: relative;
            flex-grow: 1;
            display: flex;
            justify-content: center;

            &::before,
            &::after {
                content: "";
                position: relative;
                top: calc((${numberSize} / 2) - (${lineSize} / 2));
                flex-grow: 1;
                height: ${lineSize};
                transition: ${transitionHoverStyles("background-color")};
            }

            &::before {
                background-color: var(--line-color-left);
            }

            &::after {
                background-color: var(--line-color-right);
            }

            &:first-child {
                &::before,
                .step-content::before {
                    display: none;
                }
            }

            &:last-child {
                &::after,
                .step-content::after {
                    display: none;
                }
            }

            &.active {
                --number-box-color: ${getColor("primary")};
                --number-background-color: ${getColor("primary.lightest")};
                --number-text-color: ${getColor("primary")};
                --number-border-width: 2px;
                --label-color: ${getColor("primary")};
                --line-color-left: ${getColor("primary")};
            }

            &.complete {
                --number-box-color: ${getColor("primary")};
                --number-background-color: ${getColor("primary")};
                --number-text-color: ${getColor("invert")};
                --label-color: ${getColor("text.main")};
                --line-color-left: ${getColor("primary")};
                --line-color-right: ${getColor("primary")};
            }
        `,
    },
    stepContent: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;

            &::before,
            &::after {
                content: "";
                position: absolute;
                top: calc((${numberSize} / 2) - (${lineSize} / 2));
                width: 50%;
                left: 0;
                height: ${lineSize};
                transition: ${transitionHoverStyles("background-color")};
            }

            &::before {
                background-color: var(--line-color-left);
            }

            &::after {
                left: 50%;
                background-color: var(--line-color-right);
            }
        `,
    },
    completeIcon: {
        size: 16,
        src: Checkmark,
        css: css`
            color: var(--number-text-color);
        `,
    },
    stepNumber: {
        css: css`
            position: relative;
            z-index: 1;
            display: flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            width: ${numberSize};
            height: ${numberSize};
            font-size: ${x => x.theme.design.typography.size.base};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            color: var(--number-text-color);
            background-color: var(--number-background-color);
            border: var(--number-border-width) solid var(--number-box-color);
            border-radius: 50%;
            transition: ${transitionHoverStyles("color", "background-color", "border-color", "border-width")};
        `,
    },
    stepLabel: {
        css: css`
            margin-top: ${pxToRem(8)};
            padding: 0 ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            color: var(--label-color);
            text-align: center;
            transition: ${transitionHoverStyles("color")};
        `,
    },
};

const ProgressStepper: FC<ProgressStepperProps> = ({
    activeStep: baseActiveStep,
    extendedStyles,
    overridedStyles,
    steps,
}) => {
    const styles = expandProps(progressStepperStyles, extendedStyles, overridedStyles);
    const activeStep = baseActiveStep && baseActiveStep > 0 ? baseActiveStep : undefined;

    return (
        <StyledWrapper {...styles.wrapper} as="ol">
            {steps.map((label, index) => {
                const stepNumber = index + 1;
                const isLastStep = stepNumber === steps.length;
                // The final step (e.g. "Request Submitted") represents a completed flow rather than
                // an in-progress one, so it should show the checkmark once it becomes active.
                const isComplete =
                    !!activeStep && (stepNumber < activeStep || (isLastStep && stepNumber === activeStep));
                const isActive = !!activeStep && stepNumber === activeStep;

                return (
                    <StyledWrapper
                        key={stepNumber}
                        {...styles.stepWrapper}
                        as="li"
                        aria-current={isActive ? "step" : undefined}
                        className={isComplete ? "complete" : isActive ? "active" : undefined}
                    >
                        <StyledWrapper {...styles.stepContent} className="step-content">
                            <Typography {...styles.stepNumber} aria-hidden="true">
                                {isComplete ? <Icon {...styles.completeIcon} /> : stepNumber}
                            </Typography>
                            <Typography {...styles.stepLabel}>{label}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

export default ProgressStepper;
